import { useMemo, useState } from 'react';
import VerifyStartIngestionModal from './components/VerifyStartIngestionModal';
import FullIngestionButton from './components/FullIngestionButton';
import ScheduleIconComponent from '@3.0/Studio/Ingestion/ScheduleIconComponent';
import IngestionStatusLabel from './components/IngestionStatusLabel/IngestionStatusLabel';
import { useFetchIngestionLogs } from '../hooks/useIngestionLogSummary.query';
import { getValidLogs } from '../utilities/getValidLogs';
import { useFullIngestionActions } from '../hooks/useFullIngestionActions.query';
import { useFullIngestionStatus } from '../hooks/useFullIngestionStatus.query';
import { useGlobalIngestionSchedule } from '../hooks/useGlobalIngestionSchedule';
import { useIngestionSchedule } from './context/IngestionSchedule.context';
import { ShowComponentIfUserHasPermission } from 'RBAC/index';
import { StudioRoutes } from 'Routing/RouteConstants';
import { useDSIngestionStatus } from '../hooks/useDSIngestionStatus.query';
import ControlBarButton from '@3.0/Buttons/ControlBarButtons/ControlBarButton';
import { useIngestionDataSourceList } from '../hooks/useIngestionDataSourceList.query';
import {
  FULL_INGESTION_LOG_NAME,
  IOnAddDismissibleLinkProp,
} from '../constants/ingestion.constants';
import ViewLogButton from './IngestionByDataSource/components/ViewLogButton';
import { useAppSelector } from '@redux/store';
import { selectHasPermissions } from '@redux/Roles/roles.selectors';

function FullIngestion({ onAddDismissibleLink }: IOnAddDismissibleLinkProp) {
  const { data: logs } = useFetchIngestionLogs();
  const { data: dataSourceList } = useIngestionDataSourceList();
  const canEdit = useAppSelector((state) =>
    selectHasPermissions(state, StudioRoutes.ingestion.permission)
  );
  const { ingestionScheduleIsOpen, setIngestionScheduleIsOpen } = useIngestionSchedule();
  const [isStartIngestionModalOpen, setIsStartIngestionModalOpen] = useState(false);
  const { scheduleExists } = useGlobalIngestionSchedule();

  const { runIngestion, stopIngestion } = useFullIngestionActions();
  const { data: ingestionData, isRunning, status, isStopping } = useFullIngestionStatus();
  const { isRunning: dsIngestionRunning } = useDSIngestionStatus();

  const handleIngestion = () => {
    setIsStartIngestionModalOpen(false);
    runIngestion();
  };

  const lastValidFullIngestion = useMemo(() => {
    if (!logs || !dataSourceList) return null;
    const { validLogs } = getValidLogs(logs, dataSourceList);
    const fullIngestionLog = validLogs.get(FULL_INGESTION_LOG_NAME);
    return fullIngestionLog;
  }, [logs, dataSourceList]);

  return (
    <header className="tw:shrink-0 tw:h-[40px] tw:w-[100%] tw:flex tw:flex-row tw:justify-between tw:items-center tw:bg-primary-200">
      <div className="tw:pl-6 tw:h-10 tw:border-primary-600/25 tw:border-b-1 tw:flex tw:justify-start tw:items-center tw:flex-1">
        <span className="tw:text-base tw:font-normal">Full Ingestion</span>
      </div>
      {isStartIngestionModalOpen && (
        <VerifyStartIngestionModal
          onClose={() => setIsStartIngestionModalOpen(false)}
          onConfirm={handleIngestion}
        />
      )}

      <span className="tw:flex tw:flex-row">
        <div className="tw:pr-[11px] tw:flex tw:flex-row tw:items-center tw:justify-center tw:gap-2">
          <IngestionStatusLabel
            status={status}
            latestIngestionLog={lastValidFullIngestion}
            activeIngestion={null} // hard coded to null for the full ingestion use case
          />
        </div>
        <ShowComponentIfUserHasPermission permissionRef={StudioRoutes.ingestion.permission}>
          <FullIngestionButton
            disabled={!dataSourceList?.used?.length || dsIngestionRunning || isStopping}
            onClick={() => {
              if (isRunning && ingestionData?.ingestionId) {
                stopIngestion(ingestionData.ingestionId);
              } else {
                setIsStartIngestionModalOpen(true);
              }
            }}
            isIngestionRunning={isRunning}
          />
        </ShowComponentIfUserHasPermission>
        <ViewLogButton showTitle onAddDismissibleLink={onAddDismissibleLink} />

        {(canEdit || scheduleExists) && (
          <ControlBarButton
            onClick={() => {
              setIngestionScheduleIsOpen(!ingestionScheduleIsOpen);
            }}
            data-testid="schedule-ingestion"
            className={'tw:h-10'}
          >
            <ScheduleIconComponent filled={scheduleExists} style={{ width: 16, height: 16 }} />
          </ControlBarButton>
        )}
      </span>
    </header>
  );
}

export default FullIngestion;
